import React, { useState, useEffect } from 'react';
import { 
  Users, UserPlus, Mail, Shield, ShieldOff, Eye, EyeOff, Edit, Trash2, 
  Search, Filter, Plus, Download, MoreVertical, AlertTriangle
} from 'lucide-react';
import { UserService, User, UserStats, UserFilters } from '../services/user';
import { useAuthStore } from '../stores/authStore';
import Alert from '../components/common/Alert';
import EnhancedPagination from '../components/common/EnhancedPagination';
import { ViewablePlant } from '../types/auth';

interface PlantAccessSelectorProps {
  plants: ViewablePlant[];
  primaryPlant: string;
  selectedIds: number[];
  status: 'idle' | 'loading' | 'ready' | 'error';
  error: string | null;
  onChange: (ids: number[]) => void;
  onRetry: () => void;
}

const normalizePlant = (value?: string | null) => (value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '');

const PlantAccessSelector: React.FC<PlantAccessSelectorProps> = ({
  plants,
  primaryPlant,
  selectedIds,
  status,
  error,
  onChange,
  onRetry,
}) => {
  const primary = normalizePlant(primaryPlant);
  const options = plants.filter((plant) => !primary || ![
    plant.alv_id,
    plant.code,
    plant.name,
  ].some((identifier) => normalizePlant(identifier) === primary));

  const togglePlant = (plantId: number) => {
    onChange(selectedIds.includes(plantId)
      ? selectedIds.filter((id) => id !== plantId)
      : [...selectedIds, plantId]);
  };

  return (
    <fieldset className="rounded-lg border border-gray-300 bg-gray-50 p-3">
      <legend className="px-1 text-sm font-medium text-gray-700">
        Additional view-only plants
      </legend>
      <p className="mb-3 text-xs text-gray-500">
        The primary plant remains editable. Every plant selected here is read-only.
      </p>

      {status === 'loading' || status === 'idle' ? (
        <p className="text-sm text-gray-500">Loading available plants…</p>
      ) : status === 'error' ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          <span>Plant choices could not be loaded.{error ? ` ${error}` : ''}</span>
          <button type="button" onClick={onRetry} className="font-medium underline">
            Retry
          </button>
        </div>
      ) : options.length === 0 ? (
        <p className="text-sm text-gray-500">No additional active plants are available.</p>
      ) : (
        <div className="grid max-h-40 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
          {options.map((plant) => {
            const label = plant.alv_id || plant.code || plant.name;
            return (
              <label key={plant.id} className="flex cursor-pointer items-start gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 hover:border-primary/50">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(plant.id)}
                  onChange={() => togglePlant(plant.id)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="min-w-0 text-sm text-gray-700">
                  <span className="font-medium">{label}</span>
                  {plant.name !== label ? <span className="block truncate text-xs text-gray-500">{plant.name}</span> : null}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </fieldset>
  );
};

const UserManagement: React.FC = () => {
  const {
    accessContext,
    accessContextStatus,
    accessContextError,
    loadAccessContext,
  } = useAuthStore();
  const availablePlants = accessContext?.accessible_plants || [];
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<UserFilters>({
    page: 1,
    page_size: 20,
    ordering: 'last_name'
  });
  const [totalCount, setTotalCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  
  // UI State
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  // Create user form state
  const [createUserForm, setCreateUserForm] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    employee_id: '',
    department: '',
    plant: '',
    phone: '',
    is_active: true,
    is_staff: false,
    password: 'TempPassword123!',
    confirm_password: 'TempPassword123!',
    viewable_plant_ids: [] as number[]
  });
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [showCreateConfirmPassword, setShowCreateConfirmPassword] = useState(false);
  
  // Edit user state
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editUserForm, setEditUserForm] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    employee_id: '',
    department: '',
    plant: '',
    phone: '',
    is_active: true,
    is_staff: false,
    password: '',
    confirm_password: '',
    viewable_plant_ids: [] as number[]
  });
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [editUserLoading, setEditUserLoading] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showEditConfirmPassword, setShowEditConfirmPassword] = useState(false);
  
  // Alert state
  const [alert, setAlert] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'info' | 'success' | 'warning' | 'error';
    onConfirm?: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  // Load users and stats
  const loadUsers = async () => {
    try {
      setLoading(true);
      const [usersResponse, statsResponse] = await Promise.all([
        UserService.getUsers(filters),
        UserService.getUserStats()
      ]);
      
      setUsers(usersResponse.results);
      setTotalCount(usersResponse.count);
      setHasNext(!!usersResponse.next);
      setHasPrevious(!!usersResponse.previous);
      setStats(statsResponse);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [filters]);

  // Handle search
  const handleSearch = (searchTerm: string) => {
    setFilters(prev => ({
      ...prev,
      search: searchTerm || undefined,
      page: 1
    }));
  };

  // Handle filter changes
  const handleFilterChange = (key: keyof UserFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  // Handle page size change
  const handlePageSizeChange = (pageSize: number) => {
    setFilters(prev => ({ ...prev, page_size: pageSize, page: 1 }));
  };

  // Handle password reset
  const handleSendPasswordReset = async (user: User) => {
    try {
      const result = await UserService.sendPasswordReset(user.id);
      setAlert({
        isOpen: true,
        title: 'Email Sent',
        message: `Password reset email sent to ${result.email}`,
        type: 'success'
      });
    } catch (err: any) {
      setAlert({
        isOpen: true,
        title: 'Error',
        message: err.response?.data?.error || 'Failed to send password reset email',
        type: 'error'
      });
    }
  };

  // Handle role toggle
  const handleToggleRole = async (user: User) => {
    try {
      const result = await UserService.toggleRole(user.id);
      setUsers(prev => prev.map(u => u.id === user.id ? result.user : u));
      setAlert({
        isOpen: true,
        title: 'Role Updated',
        message: result.message,
        type: 'success'
      });
    } catch (err: any) {
      setAlert({
        isOpen: true,
        title: 'Error',
        message: err.response?.data?.error || 'Failed to update user role',
        type: 'error'
      });
    }
  };

  // Handle active toggle
  const handleToggleActive = async (user: User) => {
    try {
      const result = await UserService.toggleActive(user.id);
      setUsers(prev => prev.map(u => u.id === user.id ? result.user : u));
      setAlert({
        isOpen: true,
        title: 'Status Updated',
        message: result.message,
        type: 'success'
      });
    } catch (err: any) {
      setAlert({
        isOpen: true,
        title: 'Error',
        message: err.response?.data?.error || 'Failed to update user status',
        type: 'error'
      });
    }
  };

  // Handle user deletion
  const handleDeleteUser = (user: User) => {
    setAlert({
      isOpen: true,
      title: 'Delete User',
      message: `Are you sure you want to delete ${user.full_name}? This action cannot be undone.`,
      type: 'warning',
      onConfirm: async () => {
        try {
          await UserService.deleteUser(user.id);
          setUsers(prev => prev.filter(u => u.id !== user.id));
          setAlert({
            isOpen: true,
            title: 'User Deleted',
            message: `${user.full_name} has been deleted successfully`,
            type: 'success'
          });
        } catch (err: any) {
          setAlert({
            isOpen: true,
            title: 'Error',
            message: err.response?.data?.error || 'Failed to delete user',
            type: 'error'
          });
        }
      }
    });
  };

  // Handle edit user
  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setEditUserForm({
      username: user.username,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      employee_id: user.employee_id || '',
      department: user.department || '',
      plant: user.plant || '',
      phone: user.phone || '',
      is_active: user.is_active,
      is_staff: user.is_staff,
      password: '',
      confirm_password: '',
      viewable_plant_ids: (user.viewable_plants || []).map(plant => plant.id)
    });
    setShowPasswordFields(false);
    setShowEditPassword(false); // Reset password visibility
    setShowEditConfirmPassword(false); // Reset confirm password visibility
    setShowEditUserModal(true);
  };

  // Handle update user
  const handleUpdateUser = async () => {
    if (!editingUser) return;

    // Validate password fields if they are being changed
    if (showPasswordFields) {
      if (!editUserForm.password || !editUserForm.confirm_password) {
        setAlert({
          isOpen: true,
          title: 'Validation Error',
          message: 'Both password fields are required when changing password',
          type: 'error'
        });
        return;
      }

      if (editUserForm.password !== editUserForm.confirm_password) {
        setAlert({
          isOpen: true,
          title: 'Validation Error',
          message: 'Passwords do not match',
          type: 'error'
        });
        return;
      }

      if (editUserForm.password.length < 8) {
        setAlert({
          isOpen: true,
          title: 'Validation Error',
          message: 'Password must be at least 8 characters long',
          type: 'error'
        });
        return;
      }
    }

    setEditUserLoading(true);
    try {
      // Prepare update data
      const employeeId = editUserForm.employee_id.trim();
      const updateData = {
        username: editUserForm.username,
        email: editUserForm.email,
        first_name: editUserForm.first_name,
        last_name: editUserForm.last_name,
        employee_id: employeeId || undefined,
        department: editUserForm.department,
        plant: editUserForm.plant,
        phone: editUserForm.phone,
        is_active: editUserForm.is_active,
        is_staff: editUserForm.is_staff,
        viewable_plant_ids: editUserForm.viewable_plant_ids,
        ...(showPasswordFields && editUserForm.password ? { password: editUserForm.password } : {})
      };

      const updatedUser = await UserService.updateUser(editingUser.id, updateData);
      
      // Update the user in the list
      setUsers(prev => prev.map(u => u.id === editingUser.id ? updatedUser : u));
      
      setShowEditUserModal(false);
      setEditingUser(null);
      setShowPasswordFields(false);
      setShowEditPassword(false);
      setShowEditConfirmPassword(false);
      setAlert({
        isOpen: true,
        title: 'User Updated',
        message: `${updatedUser.full_name} has been updated successfully${showPasswordFields ? ' (including password)' : ''}`,
        type: 'success'
      });
    } catch (err: any) {
      const errorData = err.response?.data;
      let errorMessage = 'Failed to update user';
      
      if (errorData && typeof errorData === 'object') {
        // Handle field-specific errors
        const fieldErrors = [];
        
        // Handle username errors
        if (errorData.username) {
          const usernameErrors = Array.isArray(errorData.username) ? errorData.username : [errorData.username];
          fieldErrors.push(`Username: ${usernameErrors.join(', ')}`);
        }
        
        // Handle email errors
        if (errorData.email) {
          const emailErrors = Array.isArray(errorData.email) ? errorData.email : [errorData.email];
          fieldErrors.push(`Email: ${emailErrors.join(', ')}`);
        }
        
        // Handle employee ID errors
        if (errorData.employee_id) {
          const employeeIdErrors = Array.isArray(errorData.employee_id) ? errorData.employee_id : [errorData.employee_id];
          fieldErrors.push(`Employee ID: ${employeeIdErrors.join(', ')}`);
        }
        
        // Handle password errors with user-friendly messages
        if (errorData.password) {
          const passwordErrors = Array.isArray(errorData.password) ? errorData.password : [errorData.password];
          const friendlyPasswordErrors = passwordErrors.map((error: string) => {
            if (error.includes('too common')) {
              return 'Password is too common. Please choose a more unique password.';
            } else if (error.includes('too short')) {
              return 'Password must be at least 8 characters long.';
            } else if (error.includes('entirely numeric')) {
              return 'Password cannot be entirely numeric.';
            } else if (error.includes('too similar')) {
              return 'Password is too similar to your personal information.';
            } else {
              return error;
            }
          });
          fieldErrors.push(`Password: ${friendlyPasswordErrors.join(', ')}`);
        }
        
        if (fieldErrors.length > 0) {
          errorMessage = fieldErrors.join('\n');
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      }
      
      setAlert({
        isOpen: true,
        title: 'Error',
        message: errorMessage,
        type: 'error'
      });
    } finally {
      setEditUserLoading(false);
    }
  };

  // Handle single user creation
  const handleCreateUser = async () => {
    // Validate form
    if (!createUserForm.username || !createUserForm.email || !createUserForm.first_name || !createUserForm.last_name) {
      setAlert({
        isOpen: true,
        title: 'Error',
        message: 'Please fill in all required fields (Username, Email, First Name, Last Name)',
        type: 'error'
      });
      return;
    }

    if (createUserForm.password !== createUserForm.confirm_password) {
      setAlert({
        isOpen: true,
        title: 'Error',
        message: 'Passwords do not match',
        type: 'error'
      });
      return;
    }

    if (!createUserForm.email.includes('@')) {
      setAlert({
        isOpen: true,
        title: 'Error',
        message: 'Please enter a valid email address',
        type: 'error'
      });
      return;
    }

    try {
      setCreateUserLoading(true);
      // Include both password and confirm_password for backend validation
      const employeeId = createUserForm.employee_id.trim();
      const userData = {
        ...createUserForm,
        employee_id: employeeId || undefined
      };
      
      const newUser = await UserService.createUser(userData);
      
      // Add new user to the list
      setUsers(prev => [newUser, ...prev]);
      
      // Reset form and close modal
      setCreateUserForm({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        employee_id: '',
        department: '',
        plant: '',
        phone: '',
        is_active: true,
        is_staff: false,
        password: 'TempPassword123!',
        confirm_password: 'TempPassword123!',
        viewable_plant_ids: []
      });
      setShowCreateUserModal(false);
      
      setAlert({
        isOpen: true,
        title: 'Success',
        message: `User ${newUser.full_name} created successfully`,
        type: 'success'
      });
      
      // Reload to get updated stats
      loadUsers();
      
    } catch (err: any) {
      let errorMessage = 'Failed to create user';
      
      if (err.response?.data) {
        const errorData = err.response.data;
        
        // Handle validation errors
        if (errorData.password) {
          const passwordErrors = Array.isArray(errorData.password) ? errorData.password : [errorData.password];
          const friendlyPasswordErrors = passwordErrors.map((error: string) => {
            if (error.includes('too common')) {
              return 'Password is too common. Please choose a more unique password.';
            } else if (error.includes('too short')) {
              return 'Password must be at least 8 characters long.';
            } else if (error.includes('entirely numeric')) {
              return 'Password cannot be entirely numeric.';
            } else if (error.includes('too similar')) {
              return 'Password is too similar to your personal information.';
            } else {
              return error;
            }
          });
          errorMessage = `Password: ${friendlyPasswordErrors.join(', ')}`;
        } else if (errorData.username) {
          const usernameErrors = Array.isArray(errorData.username) ? errorData.username : [errorData.username];
          errorMessage = `Username: ${usernameErrors.join(', ')}`;
        } else if (errorData.email) {
          const emailErrors = Array.isArray(errorData.email) ? errorData.email : [errorData.email];
          errorMessage = `Email: ${emailErrors.join(', ')}`;
        } else if (errorData.non_field_errors) {
          errorMessage = errorData.non_field_errors.join(', ');
        } else if (errorData.message || errorData.error) {
          errorMessage = errorData.message || errorData.error;
        }
      }
      
      setAlert({
        isOpen: true,
        title: 'Error',
        message: errorMessage,
        type: 'error'
      });
    } finally {
      setCreateUserLoading(false);
    }
  };


  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600">Manage users and their permissions</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowCreateUserModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700"
          >
            <UserPlus className="h-4 w-4" />
            <span>Add User</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm text-gray-600">Total Users</p>
                <p className="text-xl font-semibold">{stats.total_users}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                <div className="h-3 w-3 bg-green-600 rounded-full"></div>
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-xl font-semibold">{stats.active_users}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                <div className="h-3 w-3 bg-gray-600 rounded-full"></div>
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-600">Inactive</p>
                <p className="text-xl font-semibold">{stats.inactive_users}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm text-gray-600">Admins</p>
                <p className="text-xl font-semibold">{stats.admin_users}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-gray-600" />
              <div className="ml-3">
                <p className="text-sm text-gray-600">Normal Users</p>
                <p className="text-xl font-semibold">{stats.normal_users}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              value={filters.search || ''}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={filters.is_active === undefined ? '' : filters.is_active.toString()}
                  onChange={(e) => handleFilterChange('is_active', e.target.value === '' ? undefined : e.target.value === 'true')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                >
                  <option value="">All</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={filters.is_staff === undefined ? '' : filters.is_staff.toString()}
                  onChange={(e) => handleFilterChange('is_staff', e.target.value === '' ? undefined : e.target.value === 'true')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                >
                  <option value="">All</option>
                  <option value="true">Admin</option>
                  <option value="false">User</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <input
                  type="text"
                  placeholder="Filter by department..."
                  value={filters.department || ''}
                  onChange={(e) => handleFilterChange('department', e.target.value || undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                <select
                  value={filters.ordering || 'last_name'}
                  onChange={(e) => handleFilterChange('ordering', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                >
                  <option value="last_name">Name (A-Z)</option>
                  <option value="-last_name">Name (Z-A)</option>
                  <option value="email">Email (A-Z)</option>
                  <option value="-email">Email (Z-A)</option>
                  <option value="-date_joined">Newest First</option>
                  <option value="date_joined">Oldest First</option>
                  <option value="-last_login">Last Login</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {user.first_name?.[0]}{user.last_name?.[0]}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.is_admin 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {user.is_admin && <Shield className="h-3 w-3 mr-1" />}
                      {user.role_display}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {UserService.formatUserStatus(user)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.department || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {UserService.formatLastLogin(user.last_login)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleSendPasswordReset(user)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded"
                        title="Send Password Reset"
                      >
                        <Mail className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditUser(user)}
                        className="text-gray-600 hover:text-gray-900 p-1 rounded"
                        title="Edit User"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleToggleRole(user)}
                        className="text-purple-600 hover:text-purple-900 p-1 rounded"
                        title={`Make ${user.is_admin ? 'User' : 'Admin'}`}
                      >
                        {user.is_admin ? <ShieldOff className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleToggleActive(user)}
                        className={`p-1 rounded ${
                          user.is_active 
                            ? 'text-red-600 hover:text-red-900' 
                            : 'text-green-600 hover:text-green-900'
                        }`}
                        title={user.is_active ? 'Deactivate' : 'Activate'}
                      >
                        <AlertTriangle className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user)}
                        className="text-red-600 hover:text-red-900 p-1 rounded"
                        title="Delete User"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Enhanced Pagination */}
        <EnhancedPagination
          currentPage={filters.page || 1}
          totalCount={totalCount}
          pageSize={filters.page_size || 20}
          hasNext={hasNext}
          hasPrevious={hasPrevious}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={[10, 20, 50, 100]}
          showPageSizeSelector={true}
          showPageNumbers={true}
        />
      </div>


      {/* Create User Modal */}
      {showCreateUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Create New User</h2>
              <button
                onClick={() => {
                  setShowCreateUserModal(false);
                  setCreateUserForm({
                    username: '',
                    email: '',
                    first_name: '',
                    last_name: '',
                    employee_id: '',
                    department: '',
                    plant: '',
                    phone: '',
                    is_active: true,
                    is_staff: false,
                    password: 'TempPassword123!',
                    confirm_password: 'TempPassword123!',
                    viewable_plant_ids: []
                  });
                  setShowCreatePassword(false);
                  setShowCreateConfirmPassword(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Username */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={createUserForm.username}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, username: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="john.doe"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={createUserForm.email}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="john.doe@autoliv.com"
                  />
                </div>

                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={createUserForm.first_name}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, first_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="John"
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={createUserForm.last_name}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, last_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Doe"
                  />
                </div>

                {/* Employee ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee ID
                  </label>
                  <input
                    type="text"
                    value={createUserForm.employee_id}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, employee_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="EMP001"
                  />
                </div>

                {/* Department */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department
                  </label>
                  <input
                    type="text"
                    value={createUserForm.department}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, department: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Engineering"
                  />
                </div>

                {/* Plant */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plant
                  </label>
                  <input
                    type="text"
                    value={createUserForm.plant}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, plant: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="SWN"
                  />
                </div>

                {/* Additional view-only plant access */}
                <div className="md:col-span-2">
                  <PlantAccessSelector
                    plants={availablePlants}
                    primaryPlant={createUserForm.plant}
                    selectedIds={createUserForm.viewable_plant_ids}
                    status={accessContextStatus}
                    error={accessContextError}
                    onChange={(viewablePlantIds) => setCreateUserForm(prev => ({
                      ...prev,
                      viewable_plant_ids: viewablePlantIds,
                    }))}
                    onRetry={() => void loadAccessContext(true)}
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={createUserForm.phone}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="+216 XX XXX XXX"
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showCreatePassword ? "text" : "password"}
                      value={createUserForm.password}
                      onChange={(e) => setCreateUserForm(prev => ({ ...prev, password: e.target.value }))}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Password"
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowCreatePassword(!showCreatePassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showCreatePassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum 8 characters. Avoid common passwords like "password123", "admin123", etc.
                  </p>
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showCreateConfirmPassword ? "text" : "password"}
                      value={createUserForm.confirm_password}
                      onChange={(e) => setCreateUserForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Confirm Password"
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowCreateConfirmPassword(!showCreateConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showCreateConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {createUserForm.password && createUserForm.confirm_password && createUserForm.password !== createUserForm.confirm_password && (
                    <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                  )}
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={createUserForm.is_active.toString()}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={createUserForm.is_staff.toString()}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, is_staff: e.target.value === 'true' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="false">Normal User</option>
                    <option value="true">Admin</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
              <button
                onClick={() => {
                  setShowCreateUserModal(false);
                  setCreateUserForm({
                    username: '',
                    email: '',
                    first_name: '',
                    last_name: '',
                    employee_id: '',
                    department: '',
                    plant: '',
                    phone: '',
                    is_active: true,
                    is_staff: false,
                    password: 'TempPassword123!',
                    confirm_password: 'TempPassword123!',
                    viewable_plant_ids: []
                  });
                  setShowCreatePassword(false);
                  setShowCreateConfirmPassword(false);
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                disabled={createUserLoading || !createUserForm.username || !createUserForm.email || !createUserForm.first_name || !createUserForm.last_name}
                className="px-4 py-2 bg-primary text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {createUserLoading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                <span>{createUserLoading ? 'Creating...' : 'Create User'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Edit User: {editingUser.full_name}</h2>
              <button
                onClick={() => {
                  setShowEditUserModal(false);
                  setEditingUser(null);
                  setShowPasswordFields(false);
                  setShowEditPassword(false);
                  setShowEditConfirmPassword(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Username */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editUserForm.username}
                    onChange={(e) => setEditUserForm(prev => ({ ...prev, username: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="john.doe"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={editUserForm.email}
                    onChange={(e) => setEditUserForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="john.doe@autoliv.com"
                  />
                </div>

                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editUserForm.first_name}
                    onChange={(e) => setEditUserForm(prev => ({ ...prev, first_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="John"
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editUserForm.last_name}
                    onChange={(e) => setEditUserForm(prev => ({ ...prev, last_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Doe"
                  />
                </div>

                {/* Employee ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee ID
                  </label>
                  <input
                    type="text"
                    value={editUserForm.employee_id}
                    onChange={(e) => setEditUserForm(prev => ({ ...prev, employee_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="EMP001"
                  />
                </div>

                {/* Department */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department
                  </label>
                  <input
                    type="text"
                    value={editUserForm.department}
                    onChange={(e) => setEditUserForm(prev => ({ ...prev, department: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Engineering"
                  />
                </div>

                {/* Plant */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plant
                  </label>
                  <input
                    type="text"
                    value={editUserForm.plant}
                    onChange={(e) => setEditUserForm(prev => ({ ...prev, plant: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Autoliv Tunisia"
                  />
                </div>

                {/* Additional view-only plant access */}
                <div className="md:col-span-2">
                  <PlantAccessSelector
                    plants={availablePlants}
                    primaryPlant={editUserForm.plant}
                    selectedIds={editUserForm.viewable_plant_ids}
                    status={accessContextStatus}
                    error={accessContextError}
                    onChange={(viewablePlantIds) => setEditUserForm(prev => ({
                      ...prev,
                      viewable_plant_ids: viewablePlantIds,
                    }))}
                    onRetry={() => void loadAccessContext(true)}
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={editUserForm.phone}
                    onChange={(e) => setEditUserForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="+216 XX XXX XXX"
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={editUserForm.is_active.toString()}
                    onChange={(e) => setEditUserForm(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={editUserForm.is_staff.toString()}
                    onChange={(e) => setEditUserForm(prev => ({ ...prev, is_staff: e.target.value === 'true' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="false">Normal User</option>
                    <option value="true">Admin</option>
                  </select>
                </div>
              </div>

              {/* Password Change Section */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Change Password</h3>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordFields(!showPasswordFields);
                      if (!showPasswordFields) {
                        setEditUserForm(prev => ({ ...prev, password: '', confirm_password: '' }));
                      } else {
                        // Reset password visibility when cancelling password change
                        setShowEditPassword(false);
                        setShowEditConfirmPassword(false);
                      }
                    }}
                    className="text-sm text-primary hover:text-primary-700 font-medium"
                  >
                    {showPasswordFields ? 'Cancel Password Change' : 'Change Password'}
                  </button>
                </div>

                {showPasswordFields && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* New Password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        New Password <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showEditPassword ? "text" : "password"}
                          value={editUserForm.password}
                          onChange={(e) => setEditUserForm(prev => ({ ...prev, password: e.target.value }))}
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="Enter new password"
                          minLength={8}
                        />
                        <button
                          type="button"
                          onClick={() => setShowEditPassword(!showEditPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        >
                          {showEditPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Minimum 8 characters. Avoid common passwords like "password123", "admin123", etc.
                      </p>
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Confirm Password <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showEditConfirmPassword ? "text" : "password"}
                          value={editUserForm.confirm_password}
                          onChange={(e) => setEditUserForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="Confirm new password"
                          minLength={8}
                        />
                        <button
                          type="button"
                          onClick={() => setShowEditConfirmPassword(!showEditConfirmPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        >
                          {showEditConfirmPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      {editUserForm.password && editUserForm.confirm_password && editUserForm.password !== editUserForm.confirm_password && (
                        <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
              <button
                onClick={() => {
                  setShowEditUserModal(false);
                  setEditingUser(null);
                  setShowPasswordFields(false);
                  setShowEditPassword(false);
                  setShowEditConfirmPassword(false);
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateUser}
                disabled={editUserLoading || !editUserForm.username || !editUserForm.email || !editUserForm.first_name || !editUserForm.last_name}
                className="px-4 py-2 bg-primary text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {editUserLoading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                <span>{editUserLoading ? 'Updating...' : 'Update User'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <Alert
        isOpen={alert.isOpen}
        onClose={() => setAlert(prev => ({ ...prev, isOpen: false }))}
        title={alert.title}
        message={alert.message}
        type={alert.type}
        onConfirm={alert.onConfirm}
        showCancel={!!alert.onConfirm}
      />
    </div>
  );
};

export default UserManagement;
